import { Box, Button, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useIdCheckFlow } from '../../../../logic'
import { useLocation } from 'react-router-dom'
import { ErrorDialog } from '../../dialogs/ErrorDialog'
import { QRCodeSVG } from 'qrcode.react'

export function IdCheckTask({ orderKey, notificationId }: {
  orderKey: string,
  notificationId: string,
}) {
  const { search } = useLocation()
  const {
    idCheckUrl,
    isPolling,
    getIdCheckUrlLoading,
    handleIdCheckClick,
    startPolling,
    isComplete,
    error,
    isCompatibleDevice,
    isSecondaryVerification,
    notification,
  } = useIdCheckFlow(orderKey, notificationId, search)

  const showMobileSwitchPrompt = !isCompatibleDevice && isSecondaryVerification

  if (isComplete) {
    return null
  }

  if (error) {
    return <ErrorDialog error='There was an error processing your request.' tryAgainText='Reload and try again' />
  }

  const bankName = notification?.Configuration?.SecondaryAccountVerificationConfig?.BankName
  const routingNumber = notification?.Configuration?.SecondaryAccountVerificationConfig?.RoutingNumber
  const maskedAccountNumber = notification?.Configuration?.SecondaryAccountVerificationConfig?.MaskedAccountNumber

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
      }}
    >
      {!showMobileSwitchPrompt && isSecondaryVerification && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <Typography variant="body1">
            Please confirm you wish to submit the following account information:
            <br/><br/>
            Bank Name: <strong style={{wordBreak: 'break-all'}}>{bankName}</strong>
            <br/>
            Routing Number: <strong>{routingNumber}</strong>
            <br/>
            Account Number: <strong>{maskedAccountNumber}</strong>
            <br/><br/>
            Click verify your identity to complete your account submission.
          </Typography>
        </Box>
      )}

      {!showMobileSwitchPrompt && (
        <LoadingButton
          loading={getIdCheckUrlLoading}
          variant="contained"
          disabled={!isCompatibleDevice && isSecondaryVerification}
          sx={{
            borderRadius: "40px",
            minWidth: "250px",
          }}
          onClick={handleIdCheckClick}
        >
          Verify your identity
        </LoadingButton>
      )}

      {showMobileSwitchPrompt && (
        <>
          <Typography variant="body1">
            As part of this verification you will need your government issued ID (US driver's license, US ID card or
            Passport). You must complete this verification on a mobile device running iOS 11+ or Android 5.1+.
            <br/><br/>
            Please scan the QR code below with your mobile device camera to complete the verification process.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: "20px", border: '2px solid black' }}>
            <QRCodeSVG value={window.location.href} size={256} />
          </Box>
        </>
      )}

      {!showMobileSwitchPrompt && idCheckUrl && !isPolling && (
        <Button
          variant="outlined"
          sx={{
            borderRadius: "40px",
            minWidth: "250px",
          }}
          onClick={startPolling}
        >
          I've completed verification
        </Button>
      )}

      {!showMobileSwitchPrompt && idCheckUrl && (
        <Box sx={{ mt: 2 }}>
          If a new window did not open you can allow pop-ups or&nbsp;
          <a href={idCheckUrl} target='_blank' rel='noreferrer'>
            click here to verify your identity in a new window.
          </a>
        </Box>
      )}
    </Box>
  )
}
